/**
 * THE WORDS ON THE CARD — which of a task's questions a customer reads VERBATIM, in one
 * language, and which of them nobody has written yet.
 *
 * PURE. No Vue, no store, no I/O, no clock. Both screens that show this — the ghost line
 * under a step's question box, and the sweep screen that fixes a whole task at once —
 * read it from here, so the count on the card, the count in the rail and the state of one
 * box can never disagree.
 *
 * ── WHY THIS FILE EXISTS ──────────────────────────────────────────────────────────────
 * A task's questions reach a customer in two completely different ways, and the whole of
 * this file is the difference between them:
 *
 *   • IN A CHAT, the assistant phrases every question itself, in whatever language the
 *     customer wrote in. `instruction` is written for the model and the model does the
 *     rest. Nothing here applies.
 *   • ON A CARD (a form inside WhatsApp), there is no model. Whatever a field is labelled
 *     with is sent exactly as it was typed. So a card can only be built in a language
 *     somebody actually wrote the words in.
 *
 * `services/ai-sidecar/src/tasks/flow.ts` is the one place that decides this, and every
 * rule below is read off it rather than remembered:
 *
 *   1. ONLY `ask` AND `choose` CAN BE A FIELD. `schema.ts:84-96` spreads `questionEn` /
 *      `questionAr` into exactly those two and deliberately not into `choose_live`, whose
 *      docblock says a question there "would be a box the owner fills for nothing" —
 *      its rows are fetched while the conversation is running, so it can never be a
 *      static field (`flow.ts` `placeOf` → `runtime_rows`). A `call`, a `tell`, a
 *      hand-over and an ending are refused for their own reasons and carry no question
 *      field at all.
 *   2. AN `ask` THAT WANTS A TIME OF DAY CANNOT BE A FIELD EITHER, in any language:
 *      `askField` refuses `kind: 'time'` before it ever looks at the words
 *      (`flow.ts` — "a form has no time picker"). A box for it would be as empty a
 *      chore as a box on a live list.
 *   3. A STEP WITH NO WRITTEN QUESTION STILL HAS ONE, sometimes. `questionFor` falls back
 *      to the step's own `name` when the name is written in the language being built —
 *      which is why the live sixteen-step booking task builds an English card out of
 *      names like "Which studio" while its Arabic card does not exist at all. A screen
 *      that called those steps "missing in English" would raise a warning the owner can
 *      never clear by writing anything, so `from_name` is a state of its own.
 *   4. AN OPTION WITH NO LABEL IN THIS LANGUAGE IS NOT THE SAME FAILURE. `optionTitle`
 *      falls back to the other language's label, so the card is still built — the
 *      customer simply reads that one row in the wrong script. Only an option with words
 *      in NEITHER language refuses the card (`no_label`). Two different sentences, so
 *      two different states.
 *
 * ── WHAT "MISSING" MEANS, AND WHAT IT COSTS ───────────────────────────────────────────
 * `missing` counts the words somebody still has to type: a question with nothing written
 * and no name to fall back on, and an option label that would otherwise be read in the
 * other language. It never counts a step that could not be a field whatever she wrote,
 * because a count that cannot be driven to zero is a warning, not a task.
 */

/**
 * The Arabic script, as `text/language.ts` draws it. Kept identical on purpose — the
 * four ranges are Arabic, Arabic Supplement, and the two presentation-forms blocks.
 *
 * BUILT FROM A STRING RATHER THAN WRITTEN AS A REGEX LITERAL, and that is not style:
 * `eslint --fix` rewrites a unicode escape inside a regex LITERAL into the character it
 * names, and one of these bounds is U+FEFF — invisible, and `no-irregular-whitespace`
 * then fails on the file the fixer just produced. A string keeps its own escapes.
 */
// eslint-disable-next-line prefer-regex-literals -- a literal here is rewritten into invisible characters by `eslint --fix` itself; see the note above.
const ARABIC_RANGE = new RegExp(
  '[\\u0600-\\u06FF\\u0750-\\u077F\\uFB50-\\uFDFF\\uFE70-\\uFEFF]'
);
const LETTER = /\p{L}/u;
const LATIN = /\p{Script=Latin}/u;
/** Letters inside a link or an email address say nothing about the writer's language. */
const LINK_OR_EMAIL = /(?:https?:\/\/|www\.)\S+|\S+@\S+\.\S+/gi;

/** `schema.ts` `MAX_QUESTION`. A question longer than this is refused by the server. */
export const MAX_QUESTION = 200;
/** `schema.ts` `MAX_OPTION_LABEL`. WhatsApp's tighter shape caps a button title here. */
export const MAX_OPTION_LABEL = 20;
/** `schema.ts` `MAX_CTA`: the form's submit button, WhatsApp's Footer label cap. */
export const MAX_CTA = 35;

/** The two languages a card can be built in. */
export const LANGS = ['en', 'ar'];

export const otherLang = lang => (lang === 'ar' ? 'en' : 'ar');

/** `flow.ts` `flatten`: one line, no runs of space, nothing on either end. */
export const flatten = text =>
  typeof text === 'string' ? text.replace(/\s+/gu, ' ').trim() : '';

/**
 * Which language a single owner-written string is in, or `null` when nothing about it
 * says.
 *
 * A SCRIPT test by letter majority, and a port of `flow.ts` `scriptOf` — which is itself
 * deliberately NOT the customer-message detector, because that one refuses to claim
 * English under three words and would refuse "Which studio". A tie, and a string made
 * only of digits or punctuation, is `null`: it is not a question in either language.
 */
export const scriptOf = text => {
  const letters = [...String(text ?? '').replace(LINK_OR_EMAIL, ' ')].filter(
    ch => LETTER.test(ch)
  );
  const arabic = letters.filter(ch => ARABIC_RANGE.test(ch)).length;
  const latin = letters.filter(
    ch => !ARABIC_RANGE.test(ch) && LATIN.test(ch)
  ).length;
  if (arabic > latin) return 'ar';
  if (latin > arabic) return 'en';
  return null;
};

const questionField = lang => (lang === 'ar' ? 'questionAr' : 'questionEn');
const labelField = lang => (lang === 'ar' ? 'labelAr' : 'labelEn');

/**
 * Whether this step can ever be a field on a card, whatever anybody writes.
 *
 * The negative answers are the carve-out: a live list not shown on the form, an action, a
 * message, a hand-over, an ending, and an `ask` for a time of day. See rules 1 and 2 above.
 * A live list WITH "Show on the form" (field fixes G1, `onForm`) is a field like a choice.
 */
export const canCarryCardWords = step => {
  if (step?.type === 'choose') return true;
  // Field fixes G1: a live list the owner shows on the form is a field too; its rows come
  // from her system, but its question is read as written.
  if (step?.type === 'choose_live') return step.onForm === true;
  return step?.type === 'ask' && step.kind !== 'time';
};

/** Every step of a spine, a definition, or a bare array — whichever it was handed. */
const stepsOf = source => {
  if (Array.isArray(source)) return source.filter(Boolean);
  if (Array.isArray(source?.steps)) return source.steps.filter(Boolean);
  if (Array.isArray(source?.rows)) {
    // A spine row keeps the step body under `step` and its options under `options`, each
    // wrapped with the outcome the owner chose. The words live in the bodies.
    return source.rows
      .filter(row => row && typeof row.step === 'object' && row.step !== null)
      .map(row =>
        Array.isArray(row.options)
          ? {
              ...row.step,
              options: row.options
                .map(option => option?.option)
                .filter(option => option && typeof option === 'object'),
            }
          : row.step
      );
  }
  return [];
};

/**
 * What an option label with no words in this language costs a customer, and the two
 * answers are genuinely different:
 *   • `falls_back` — the other language's label is shown, so the card is built and one
 *     row of it reads in the wrong script (`flow.ts` `optionTitle`);
 *   • `missing` — words in NEITHER language, so there is nothing to show and the whole
 *     card is refused (`flow.ts` `no_label`).
 */
const OPTION_SEVERITY = { missing: 'blocks', falls_back: 'english' };

/** A row still wants words typed into it. `from_name` does not; it already reads right. */
export const needsWriting = row =>
  row?.state === 'missing' || row?.state === 'falls_back';

/**
 * What one task's card words look like in one language.
 *
 * @param {object|Array} source   a spine (`{rows}`), a definition (`{steps}`) or steps.
 * @param {'en'|'ar'} lang        the language being written.
 * @returns {{
 *   groups: Array<object>,  one per step that can be a field, in list order
 *   missing: number,        rows somebody still has to type
 *   total: number,          rows on the screen at all
 *   offScreen: number,      steps that could never be a field, so are not shown
 * }}
 */
export const cardWords = (source, lang = 'ar') => {
  const steps = stepsOf(source);
  const other = otherLang(lang);
  const groups = [];
  let missing = 0;
  let total = 0;

  steps.forEach((step, index) => {
    if (!canCarryCardWords(step)) return;

    const written = flatten(step[questionField(lang)]);
    const name = flatten(step.name);
    let state = 'written';
    if (written === '')
      state = scriptOf(name) === lang ? 'from_name' : 'missing';

    const question = {
      stepId: step.id ?? '',
      optionId: null,
      kind: 'question',
      lang,
      text: written,
      /** What the step reads as today, written or fallen back to — never empty by choice. */
      shown: written === '' && state === 'from_name' ? name : written,
      /** The same question in the other language, for the column beside it to translate. */
      other:
        flatten(step[questionField(other)]) ||
        (scriptOf(name) === other ? name : ''),
      name,
      state,
      max: MAX_QUESTION,
      /** `blocks` — no card at all in this language. `english` — one row in the wrong script. */
      severity: state === 'missing' ? 'blocks' : null,
    };
    total += 1;
    if (needsWriting(question)) missing += 1;

    const options = (Array.isArray(step.options) ? step.options : []).map(
      option => {
        const here = flatten(option?.[labelField(lang)]);
        const there = flatten(option?.[labelField(other)]);
        let optionState = 'written';
        if (here === '') optionState = there === '' ? 'missing' : 'falls_back';
        const row = {
          stepId: step.id ?? '',
          optionId: option?.id ?? '',
          kind: 'option',
          lang,
          text: here,
          shown: here === '' ? there : here,
          other: there,
          name: '',
          state: optionState,
          max: MAX_OPTION_LABEL,
          severity: OPTION_SEVERITY[optionState] ?? null,
        };
        total += 1;
        if (needsWriting(row)) missing += 1;
        return row;
      }
    );

    groups.push({
      stepId: step.id ?? '',
      /** Where this step sits in the whole list, so a gap in the numbers is explainable. */
      position: index + 1,
      type: step.type,
      name,
      question,
      options,
    });
  });

  return {
    groups,
    missing,
    total,
    offScreen: steps.length - groups.length,
  };
};

/** Every row of a sweep, flat and in reading order. */
export const rowsOf = sweep =>
  (sweep?.groups ?? []).flatMap(group => [group.question, ...group.options]);

/** The rows a translate pass would be asked to propose words for. */
export const missingRowsOf = sweep => rowsOf(sweep).filter(needsWriting);

/**
 * Write one row's words back into a spine row's step body, without touching anything else.
 *
 * Returns a NEW step; the caller decides where to put it. Kept here rather than in a
 * component so the one place that knows which wire field a row means is the one place
 * that decided the row existed.
 */
export const withCardWords = (step, row, text) => {
  if (!step || !row) return step;
  if (row.kind === 'question') {
    return { ...step, [questionField(row.lang)]: text };
  }
  const field = labelField(row.lang);
  return {
    ...step,
    options: (Array.isArray(step.options) ? step.options : []).map(option =>
      option?.id === row.optionId ? { ...option, [field]: text } : option
    ),
  };
};
